import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {TrackComponentParamsType, useTrackComponent} from '@aboutyou/tracking';
import {
  EntityList,
  HorizontalSliderHeader,
  HorizontalSliderProps,
  ProductDetailsWrapper,
  ProductTileImageWrapper,
  SliderWrapper,
  StyledProductImage,
  mediaDesktopAndUp,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {Fragment, useMemo} from 'react';
import {ProductTile, ProductTileProps} from '../ProductTile/ProductTile';
import {AddToBasketPayload} from '../ProductTile/types';

export interface ProductTileConfig {
  gridColumns?: number;
  onColorClick?: (colorProductId: number) => void;
  addCtaLabel?: string;
}

interface ProductListProps {
  className?: string;
  testId?: string;
  uniqueKey?: string;
  streamPosition: number;
  type: 'grid' | 'list';
  products: ProductTileV2[];
  AdditionalTile?: React.ReactNode;
  onAddToBasketCtaClick?: ({
    productId,
    couponPromotionId,
  }: {
    productId: number;
    couponPromotionId?: string;
  }) => void;
  promotionTracker?: PromotionTracker;
  trackComponentConfig: TrackComponentParamsType;
  productTileConfig?: ProductTileConfig;
  columns?: {small: number; medium: number; large: number};
  ProductTileComponent?: React.FC<ProductTileProps>;
  sliderProps?: HorizontalSliderProps;
  onProductTileClick?: VoidFunction;
}

export const ProductList: React.FC<ProductListProps> = ({
  className,
  testId,
  uniqueKey = '',
  streamPosition,
  type,
  products,
  AdditionalTile,
  onAddToBasketCtaClick,
  promotionTracker,
  trackComponentConfig,
  productTileConfig,
  columns,
  ProductTileComponent = ProductTile,
  sliderProps,
  onProductTileClick,
}) => {
  const trackComponent = useTrackComponent();

  const handleAddCtaClick = (payload: AddToBasketPayload) => {
    trackComponent({
      action: 'click',
      content_type: 'button',
      name: 'add_to_basket_product_tile',
      object_type: 'product',
      object_id: payload.product.id,
      location_id: trackComponentConfig.location_id,
      page_type_id: trackComponentConfig.page_type_id,
      creative_name: promotionTracker?.creativeName,
      sponsored: promotionTracker?.creativeName === 'ad',
      promotion_id: promotionTracker?.promotionId,
    });

    onAddToBasketCtaClick?.({
      productId: payload.product.id,
      couponPromotionId: payload.couponPromotionId,
    });
  };

  const trackingElements = useMemo(
    () =>
      products.map(tile => ({id: tile.productId, type: 'product' as const})),
    [products],
  );

  const renderedProducts = useMemo(() => {
    const nodes = products.map(tile => (
      <ProductTileComponent
        trackingData={{
          sectionIndex: streamPosition,
          recoType: promotionTracker?.promotionName,
          promotionId: promotionTracker?.promotionId,
          creativeName: promotionTracker?.creativeName,
          elements: trackingElements,
        }}
        key={tile.productId}
        tile={tile}
        onClickAddCta={onAddToBasketCtaClick ? handleAddCtaClick : undefined}
        streamPosition={streamPosition}
        gridColumns={productTileConfig?.gridColumns}
        onColorClick={productTileConfig?.onColorClick}
        addCtaLabel={productTileConfig?.addCtaLabel}
        onProductTileClick={onProductTileClick}
      />
    ));

    if (AdditionalTile) {
      nodes.push(<Fragment key={products.length}>{AdditionalTile}</Fragment>);
    }

    return nodes;
  }, [products]);

  if (type === 'grid') {
    return (
      <EntityList
        className={className}
        type="grid"
        columns={columns ?? {small: 2, medium: 2, large: 3}}
        testId={testId}
        uniqueKey={uniqueKey}
      >
        {renderedProducts}
      </EntityList>
    );
  }

  return (
    <SliderTileWrapper className={className}>
      <StyledSliderEntityList
        testId={testId}
        type="slider"
        disableHeaderControl
        hideSideButtons
        slideCount={2.2}
        slideCountSmall={2.5}
        slideCountMedium={4}
        slideCountLarge={5}
        uniqueKey={uniqueKey}
        {...sliderProps}
      >
        {renderedProducts}
      </StyledSliderEntityList>
    </SliderTileWrapper>
  );
};

const StyledSliderEntityList = styled(EntityList)`
  ${HorizontalSliderHeader} {
    margin: 0;
  }

  @media ${mediaDesktopAndUp} {
    ${SliderWrapper} {
      padding-bottom: 48px;
      margin-bottom: -48px;
    }
  }
`;

const SliderTileWrapper = styled.div`
  ${mobileOnlyLinaria(`
    ${StyledProductImage} {
      border-radius: 4px;
    }

    ${ProductTileImageWrapper} {
      background-color: transparent;
    }

    ${ProductDetailsWrapper} {
      padding: 8px 12px 0 12px;
    }
  `)}
`;
